import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import {
  Layout,
  Typography,
  message,
  Button,
  Form,
  Upload,
  Spin,
  Row,
  Col,
  Modal,
  Drawer,
  Tooltip,
  Switch,
  Image,
  Select,
} from "antd";
import { CloseOutlined, SaveOutlined, EditOutlined } from "@ant-design/icons";
import { DocumentEditor } from "@/components/DocumentEditor";
import EditModeDropdown from "./components/EditModeDropdown";
import FieldManager from "./components/FieldManager";
import FieldInfoModal from "./components/FieldInfoModal";
import LeftToolbar from "./components/LeftToolbar";
import {
  checkAndUpdateCheckboxes,
  convertToTreeData,
  getCurrentConnector,
  getCurrentEditor,
  handleRemoveHighlight,
  handleSetLongText,
  saveDocument,
} from "./utils";
import {
  CALLBACK_URL,
  DOC_URL,
  EXCEL_URL,
  domainConfig,
  ONLYOFFICE_HOST,
} from "./config/sysConfig";
import { getDomainMap, getFieldConfigByTag } from "./utils/field";
import styles from "./index.module.less";
import { DocumentCompileFileStatus, DocumentCompileFileStatusOptions, DocumentFileType, DocumentSettingType, DomainTreeNodeData, FieldType, SaveStatusEnum } from "@/pages/documentCompile/type";
import { useSetState, useUnmount } from "ahooks";
import useStore from "../store/useStore";
import { useShallow } from "zustand/react/shallow";
import useMethods from "./hooks/usemethods";
import { getDictTableData } from "../dict/utils";
import { DictTemplateCodeEnum } from "../dict/type";
import useInitData from "./hooks/useInitData";
import DomainValueSetting from "./components/FieldManager/components/DomainValueSetting";
import clsx from "clsx";
import { updateProjectDoc, updateSignStatus } from "../api/data-project-doc";
import { updateDocumentTemplate } from "../api/document-project";
import { confirmAsync, confirmWithThreeButtons } from "@/utils/antd-help";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { getRandowLangUUid, getStorage } from "@/utils/tools";
import { getUrl, ResCodeEnum } from "@/utils/request";
import qs from "qs";
import usePollingWithIdleCheck from "@/common/hooks/usePollingWithIdleCheck";
import { updateProjectDocJuan } from "../api/data-project-view";
import cloneDeep from "lodash-es/cloneDeep";
import { traverseTree } from "@/utils/tree";

const { Header, Content, Sider } = Layout;
const { Title } = Typography;
interface IProps {
  open: boolean
  onCancel: () => void
  onAsyncFieldSuccess?: () => void
  onOk: (data: any) => void
  onSaveSuccess?: (data: { fileUrl: string, }) => void
}
export const EDITOR_ID = "docxEditor";
const Index = (props: IProps) => {
  const queryClient = useQueryClient()  // 这行在你代码的开头
  const [searchParams] = useSearchParams()
  const projectName = searchParams.get('name')
  const projectId = searchParams.get('projectId')
  const requestId = searchParams.get('requestId')
  const { open, onCancel, onOk, onSaveSuccess, onAsyncFieldSuccess, } = props

  const doClose = () => {
    queryClient.invalidateQueries();  // 使所有缓存失效
    setStoreState({ documentReady: false })
    onCancel?.();
  }
  const setStoreState = useStore.setState;
  const { documentSettingProjectData, documnetSettingType, settingProjectTitle, documentType, fieldValues, selectedToolBar, editorConfig, editorConfigLoading, resetDocumentStore, isEditMode, needLoadDomainConfig, domainFieldTypeModalVisible, clickedFieldInfo, documentReady } = useStore(
    useShallow((s) => ({
      documnetSettingType: s.documnetSettingType,
      documentType: s.documentType,
      documentLoading: s.documentLoading,
      fieldValues: s.fieldValues,
      selectedToolBar: s.selectedToolBar,
      editorConfig: s.editorConfig,
      editorConfigLoading: s.editorConfigLoading,
      resetDocumentStore: s.resetDocumentStore,
      settingProjectTitle: s.settingProjectTitle,
      documentSettingProjectData: s.documentSettingProjectData,
      isEditMode: s.isEditMode,
      needLoadDomainConfig: s.needLoadDomainConfig,
      domainFieldTypeModalVisible: s.domainFieldTypeModalVisible,
      clickedFieldInfo: s.clickedFieldInfo,
      documentReady: s.documentReady,
    }))
  );



  // 获取文档ID和requestId（用于锁机制）
  const documentId = useMemo(() => {
    return documentSettingProjectData?.documentDetail?.id?.toString() || null;
  }, [documentSettingProjectData?.documentDetail?.id]);




  // console.log("projectDetail", projectDetail);
  const { handleMessage, handleFieldDoubleClick, generateEditorConfig } = useMethods();
  const { initDomainSettingData, refreshPackageFolderTreeData } = useInitData();

  const isUnmountedRef = useRef(false);


  //  构建全局 connector
  const domainMapRef = useRef<any>(null);
  console.log("domainMap", domainMapRef.current);
  // 新增：上传处理函数
  // 处理域值变化
  const handleFieldValueChange = (fieldId: string, value: any) => {
    console.log("域值变化:", fieldId, value);
    setStoreState({
      fieldValues: {
        ...fieldValues,
        [fieldId]: value,
      }
    })
  };

  const init = async () => {
    const emptyUrl = documentType === DocumentFileType.WORD ? DOC_URL : EXCEL_URL;
    const latestDocumentSettingProjectData = useStore.getState().documentSettingProjectData

    // 如果是模版类型，默认设置为编辑模式
    if (documnetSettingType === DocumentSettingType.DOCUMENT_TEMPLATE) {
      setStoreState({ isEditMode: true });
    }

    const queryParams = {
      fileUrl: '',
      fileName: '',
      mode: (documnetSettingType === DocumentSettingType.DOCUMENT_TEMPLATE ? true : isEditMode) ? 'edit' as const : 'view' as const  // 模版类型强制为编辑模式
    }
    if (documnetSettingType === DocumentSettingType.DOCUMENT_TEMPLATE) {
      queryParams.fileName = latestDocumentSettingProjectData?.documentDetail?.documentName
      queryParams.fileUrl = latestDocumentSettingProjectData?.documentDetail?.documentUrl || latestDocumentSettingProjectData?.documentDetail?.docUrl || emptyUrl
    }
    if (documnetSettingType === DocumentSettingType.DATA_PROJECT_FILE) {
      queryParams.fileName = latestDocumentSettingProjectData?.documentDetail?.fileName
      queryParams.fileUrl = latestDocumentSettingProjectData?.documentDetail?.docUrl || emptyUrl
    }
    // 设置编辑器配置加载状态
    setStoreState({ editorConfigLoading: true, documentReady: false });

    const promiseList = []
    promiseList.push(generateEditorConfig(queryParams))
    try {
      const res = await Promise.all(promiseList)
      const [editorConfig] = res
      if (editorConfig) {
        setStoreState({ editorConfig, editorConfigBack: { ...editorConfig } });
        initDomainSettingData(open, true)
      }
    } catch (error) {
      console.error('初始化失败:', error);
      message.error('加载文档配置失败');
    } finally {
      setStoreState({ editorConfigLoading: false });
    }
  }

  const handleCancel = async () => {
    const isOnlyOfficeDocumentModified = useStore.getState().isOnlyOfficeDocumentModified
    const selectedFileData = useStore.getState().selectedFileData
    if (isOnlyOfficeDocumentModified) {
      const result = await confirmWithThreeButtons({
        title: '文档已修改',
        content: '是否需要保存更改？',
      });

      if (result === 'cancel') {
        return;
      }

      if (result === 'noSave') {
        doClose?.();
        return;
      }

      if (result === 'ok') {
        const latestDocumentSettingProjectData = useStore.getState().documentSettingProjectData
        const fileName = latestDocumentSettingProjectData.documentDetail.fileName || latestDocumentSettingProjectData.documentDetail.documentName || latestDocumentSettingProjectData.documentDetail.name;
        // console.log("fileName", { editorConfig, fileName, latestDocumentSettingProjectData })
        setStoreState({
          editorConfigLoading: true,
        })
        // console.log("saveDocument", { editorConfig, selectedFileData, fileName, })
        const editorConfigBack = useStore.getState().editorConfigBack
        const saveResult = await saveDocument({
          fileName: fileName,
          editorConfig: editorConfigBack,
          showMessage: false, // 不显示保存成功消息
        });
        // console.log("saveResult", saveResult)
        if (!saveResult.modify) {
          message.warning("文档未修改");
          setStoreState({
            editorConfigLoading: false,
          });
          return;
        }
        if (!saveResult.success) {
          message.error("保存文档失败，无法进行同步");
          setStoreState({
            editorConfigLoading: false,
          })
          return;
        }
        // 保存成功后，更新文档信息
        if (latestDocumentSettingProjectData?.type === DocumentSettingType.DATA_PROJECT_FILE) {
          try {
            if (selectedFileData.isJuan) {
              await updateProjectDocJuan({ viewId: selectedFileData.viewId, docUrl: saveResult.fileUrl, }, requestId, projectId)
            } else {
              const params = {
                "docId": latestDocumentSettingProjectData.documentDetail.id,
                "docName": fileName,
                "docUrl": saveResult.fileUrl,
                "projectId": latestDocumentSettingProjectData.documentDetail.projectId,
              }
              await updateProjectDoc(params, requestId)
            }
          } catch (error) {
            setStoreState({
              editorConfigLoading: false,
            })
          }

        } else if (latestDocumentSettingProjectData?.type === DocumentSettingType.DOCUMENT_TEMPLATE) {
          // const params = {
          //   "docId": latestDocumentSettingProjectData.documentDetail.id,
          //   "docName": fileName,
          //   "docUrl": saveResult.fileUrl
          // }
          await updateDocumentTemplate(latestDocumentSettingProjectData.documentDetail.id!, { documentUrl: saveResult.fileUrl })
        }
        setStoreState({
          editorConfigLoading: false,
          isOnlyOfficeDocumentModified: false,
        })
        doClose?.();
      }
    } else {
      doClose?.();
    }
  }
  /**
   * 保存文档处理函数
   * 保存前会验证是否持有文档锁，确保只有拥有编辑权限的用户才能保存
   */
  const handleSave = async () => {
    if (!documentId) {
      message.error('文档ID不存在，无法保存');
      return;
    }
    const selectedFileData = useStore.getState().selectedFileData
    const latestDocumentSettingProjectData = useStore.getState().documentSettingProjectData
    // const latestEditorConfig = useStore.getState().editorConfig
    const latestEditorConfig = useStore.getState().editorConfigBack
    // console.log("latestEditorConfig", latestEditorConfig)
    // console.log("latestDocumentSettingProjectData", latestDocumentSettingProjectData)
    const isOnlyOfficeDocumentModified = useStore.getState().isOnlyOfficeDocumentModified
    if (latestDocumentSettingProjectData?.documentDetail) {
      setStoreState({
        editorConfigLoading: true,
        documentLoading: true,
      });
      if (isOnlyOfficeDocumentModified) {
        // console.log("saveDocument", { latestEditorConfig, latestDocumentSettingProjectData, })
        const result = await saveDocument({
          fileName: latestDocumentSettingProjectData.documentDetail.fileName || latestDocumentSettingProjectData.documentDetail.documentName,
          editorConfig: latestEditorConfig,
          onSuccess: onSaveSuccess,
          isUnmountedRef: isUnmountedRef,
        });
        if (result.success && result.modify) {
          if (result.fileUrl) {
            // const params = {
            //   "docId": state.curTemp.id,
            //   "docName": state.curTemp.fileName,
            //   "docUrl": data.fileUrl
            // }
            const docName = latestDocumentSettingProjectData.documentDetail.fileName || latestDocumentSettingProjectData.documentDetail.documentName
            // const docUrl = latestDocumentSettingProjectData.documentDetail.docUrl || latestDocumentSettingProjectData.documentDetail.documentUrl
            // console.log("params", params)
            if (latestDocumentSettingProjectData?.type === DocumentSettingType.DATA_PROJECT_FILE) {
              const params = {
                "docId": latestDocumentSettingProjectData.documentDetail.id,
                "docName": docName,
                "docUrl": result.fileUrl,
                "projectId": latestDocumentSettingProjectData.documentDetail.projectId,
              }
              try {
                if (selectedFileData.isJuan) {
                  await updateProjectDocJuan({ viewId: selectedFileData.viewId, docUrl: result.fileUrl, projectId }, requestId, projectId)
                } else {
                  await updateProjectDoc(params, requestId)
                }

              } catch (error) {
                setStoreState({
                  editorConfigLoading: false,
                  documentLoading: false,
                });
              }
            }
            if (latestDocumentSettingProjectData?.type === DocumentSettingType.DOCUMENT_TEMPLATE) {
              // const params = {
              //   "docId": latestDocumentSettingProjectData.documentDetail.id,
              //   "docName": docName,
              //   "docUrl": result.fileUrl
              // }
              try {
                await updateDocumentTemplate(latestDocumentSettingProjectData.documentDetail.id!, { documentUrl: result.fileUrl })
              } catch (error) {
                setStoreState({
                  editorConfigLoading: false,
                  documentLoading: false,
                });
              }
            }
            onSaveSuccess?.({ fileUrl: result.fileUrl })
            // message.success("保存成功");
            setStoreState({
              isOnlyOfficeDocumentModified: false,
              documentSettingProjectData: {
                ...latestDocumentSettingProjectData,
                documentDetail: {
                  ...latestDocumentSettingProjectData.documentDetail,
                  docUrl: result.fileUrl
                }
              }
            })
            // initDomainSettingData(open)
            if (latestDocumentSettingProjectData?.type === DocumentSettingType.DATA_PROJECT_FILE) {
              refreshPackageFolderTreeData()
            }
          }

        } else {
          message.warning("文档未修改");
          setStoreState({
            editorConfigLoading: false,
            documentLoading: false,
            isOnlyOfficeDocumentModified: false
          });
          return;
        }
      } else {
        setStoreState({
          isOnlyOfficeDocumentModified: false
        });
        message.warning("文档未修改");
      }
      setStoreState({
        editorConfigLoading: false,
        documentLoading: false,
      });
    }
  }

  const docUrl = documentSettingProjectData?.documentDetail?.docUrl || documentSettingProjectData?.documentDetail?.documentUrl
  console.log("docUrl", docUrl)
  // useEffect(() => {
  //   // if (!docUrl) return;
  //   if (!documnetSettingType) return;
  //   const latestDocumentSettingProjectData = useStore.getState().documentSettingProjectData
  //   // const needLoadDomainConfig = useStore.getState().needLoadDomainConfig
  //   // 如果不需要加载域配置，则不执行初始化
  //   // if (documnetSettingType === DocumentSettingType.DOCUMENT_TEMPLATE) {
  //   //   setStoreState({
  //   //     // domainTreeData: DocumentSettingType.DOCUMENT_TEMPLATE,
  //   //   })
  //   // }
  //   initDomainSettingData(documnetSettingType, latestDocumentSettingProjectData, open)
  // }, [docUrl, open,needLoadDomainConfig])

  // 监听编辑模式变化，重新加载配置
  // useEffect(() => {
  //   init();
  // }, [isEditMode]);

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    domainMapRef.current = getDomainMap(domainConfig);
  }, [domainConfig]);


  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  useEffect(() => {
    init()
  }, [])

  /**
   * 组件卸载时的清理工作
   * 1. 标记组件已卸载，防止异步操作继续执行
   * 2. 释放文档锁，确保其他用户可以编辑文档
   * 3. 重置文档store状态
   */
  useUnmount(() => {
    isUnmountedRef.current = true;
    // 重置文档相关的store状态
    resetDocumentStore();
  })
  // console.log("precess", (window as any).process.env);

  const onDocumentStateChange = (event: any) => {
    // event.data 为true表示文档已被修改
    console.log("onDocumentStateChange", event)
    // if (event.data !== state.isDocumentModified) {
    //   setState({ isDocumentModified: event.data })
    // }
    setStoreState({ isOnlyOfficeDocumentModified: true })
  }

  /**
   * 处理编辑模式切换
   * 切换到编辑模式时需要获取文档锁，切换到查看模式时需要释放锁
   * @param checked - true: 编辑模式, false: 查看模式
   */
  /**
   * 处理编辑模式切换
   * 切换到编辑模式时需要获取文档锁，切换到查看模式时需要释放锁
   */
  const handleEditModeChange = (checked: boolean) => {
    if (!documentId) {
      message.error('文档ID不存在，无法切换编辑模式');
      return;
    }

    const latestEditorConfig = useStore.getState().editorConfigBack;

    if (checked) {
      // 切换到编辑模式
      setStoreState({ isEditMode: true });

      const newConfig = {
        ...latestEditorConfig,
        editorConfig: {
          ...latestEditorConfig.editorConfig,
          mode: 'edit'
        }
      };

      setStoreState({
        editorConfig: newConfig,
        editorConfigBack: { ...newConfig },
      });

      console.log('成功切换到编辑模式');
    } else {
      // 切换到查看模式
      setStoreState({ isEditMode: false });

      const newConfig = {
        ...latestEditorConfig,
        editorConfig: {
          ...latestEditorConfig.editorConfig,
          mode: 'view'
        }
      };

      setStoreState({
        editorConfig: { ...newConfig },
        editorConfigBack: { ...newConfig }
      });

      console.log('成功切换到查看模式');
    }
  }

  const handleDocumentReady = (event: any) => {
    setStoreState({ documentReady: true })
    console.log("handleDocumentReady", event)
  }

  const documentTitle = useMemo(() => {
    const fileName = documentSettingProjectData?.documentDetail?.fileName
      || documentSettingProjectData?.documentDetail?.documentName
      || documentSettingProjectData?.documentDetail?.name
    if (documnetSettingType === DocumentSettingType.DOCUMENT_TEMPLATE) {
      return fileName
    }
    return projectName
  }, [documnetSettingType, documentSettingProjectData])

  // 判断是否为图片类型
  const isImageFile = useMemo(() => {
    const fileType = editorConfig?.document?.fileType?.toLowerCase();
    return fileType === 'jpg' || fileType === 'png';
  }, [editorConfig?.document?.fileType])

  // 获取图片URL
  const imageUrl = useMemo(() => {
    if (isImageFile) {
      return editorConfig?.document?.url;
    }
    return null;
  }, [isImageFile, editorConfig?.document?.url])

  const handleSignStatusChange = async (value: number) => {
    console.log("handleSignStatusChange", value)
    await updateSignStatus({
      projectId: documentSettingProjectData?.documentDetail?.projectId,
      docId: documentSettingProjectData?.documentDetail?.id,
      signStatus: value,
    })
    const latestDocumentSettingProjectData = useStore.getState().documentSettingProjectData
    setStoreState({
      documentSettingProjectData: {
        ...latestDocumentSettingProjectData,
        documentDetail: {
          ...latestDocumentSettingProjectData?.documentDetail,
          signStatus: value
        }
      }
    })
    const packageFolderTreeData = useStore.getState().packageFolderTreeData
    const clonePackageFolderTreeData = cloneDeep(packageFolderTreeData)
    traverseTree(clonePackageFolderTreeData, (item: any) => {
      if (item.id === documentSettingProjectData?.documentDetail?.id) {
        console.log("item", item , value)
        // return { ...item, signStatus: value }
        item.signStatus = value
      }
      return item
    })
    setStoreState({ packageFolderTreeData: clonePackageFolderTreeData })
  }


  console.log("storeState", useStore.getState())

  // const loading = editorConfigLoading || !documentReady
  const loading = editorConfigLoading
  return (
    <Drawer
      style={{
        width: "100%",
        height: "100%",
      }}
      className={styles.drawer}
      width="100%"
      open={open}
      onClose={handleCancel}
      footer={null}
    >
      <Layout className={styles.mainLayout} >
        {(loading) && (
          <Spin spinning={loading} className={styles.spinWrap}></Spin>
        )}
        {/* 顶部标题 */}
        <div
          style={{
            background: "#fff",
            height: "40px",
            lineHeight: "40px",
            padding: "0 14px",
            flexShrink: 0,
            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {documentTitle}
          </Title>

          <div style={{ display: "flex", alignItems: "center", position: "absolute", right: 10 }}>
            {/* 编辑模式切换 */}
            <div style={{ marginRight: 16, display: 'flex', alignItems: 'center' }}>
              {/* <span style={{ marginRight: 8, color: 'rgba(0, 0, 0, 0.45)' }}>
                {isEditMode ? '编辑' : '查看'}
              </span> */}


              {/* 签署状态 下拉 */}
              {
                documnetSettingType === DocumentSettingType.DATA_PROJECT_FILE && !documentSettingProjectData?.documentDetail?.isJuan && (
                  <Select
                    size="small"
                    options={DocumentCompileFileStatusOptions}
                    value={documentSettingProjectData?.documentDetail?.signStatus}
                    onChange={handleSignStatusChange}
                  />
                )
              }
              {/* 上面的注释的功能，改成下面的 tooltipt提示 */}

              <EditModeDropdown
                isEditMode={isEditMode}
                documnetSettingType={documnetSettingType}
                onChange={handleEditModeChange}
              />

            </div>
            {/* 保存按钮 */}
            <Tooltip title="保存">
              <Button
                type="link"
                className={clsx("hover-icon")}
                icon={<SaveOutlined />}
                onClick={handleSave}
                style={{ color: 'rgba(0, 0, 0, 0.45)' }}
                disabled={!isEditMode} // 非编辑模式下禁用保存按钮
              />
            </Tooltip>
            {/* 关闭按钮 */}
            <Tooltip title="关闭">
              <Button type="link" className={clsx("hover-icon")} icon={<CloseOutlined />} onClick={handleCancel} style={{ color: 'rgba(0, 0, 0, 0.45)' }} />
            </Tooltip>
          </div>
        </div>

        <Layout>
          {/* 左侧工具栏 */}
          {/* 左侧域设置面板 */}
          {/* {documentType === DocumentFileType.WORD && (
            <>
              <LeftToolbar />
              <FieldManager
                onFieldValueChange={handleFieldValueChange}
              />
            </>
          )} */}
          <LeftToolbar />
          <FieldManager
            isUnmountedRef={isUnmountedRef}
            onFieldValueChange={handleFieldValueChange}
            open={open}
          />
          {/* 右侧OnlyOffice文档区域 */}
          <Content
            style={{
              background: "#fff",
              margin: 0,
              overflow: "hidden",
            }}
          >
            <div
              className={styles.documentWrap}
              style={{ height: "100%", padding: "16px 16px 0 16px" }}
            >
              {/* {state.saveLoading && (
                <Spin spinning={state.saveLoading} className={styles.spinWrap}></Spin>
              )} */}

              {editorConfig ? (
                // isImageFile ? (
                //   <div style={{
                //     display: 'flex',
                //     justifyContent: 'center',
                //     alignItems: 'center',
                //     height: '100%',
                //     padding: '20px'
                //   }}>
                //     {/* <img src={imageUrl} /> */}
                //     <Image
                //       src={imageUrl}
                //       alt="文档图片"
                //       width={'auto'}
                //       height={'100%'}
                //       style={{ 
                //         // width:'auto',
                //         // height:'100%',
                //         // maxWidth: '95%', 
                //         // maxHeight: '95%',
                //         objectFit: 'contain'
                //       }}
                //       preview={{
                //         mask: '预览图片'
                //       }}
                //     />
                //   </div>
                // ) : (
                <DocumentEditor
                  id={EDITOR_ID}
                  documentServerUrl={ONLYOFFICE_HOST}
                  config={editorConfig}
                  events_onRequestRefreshFile={(e: any) => {
                    console.log("events_onRequestRefreshFile", e)
                  }}
                  events_onDocumentStateChange={onDocumentStateChange}
                  events_onDocumentReady={handleDocumentReady}
                  events_onError={(e: any) => {
                    console.log("events_onError", e)
                    // setStoreState({ documentReady: false })
                  }}
                // onDocumentReady={handleDocumentReady}
                // onDocumentStateChange={handleDocumentStateChange}
                // onFieldClick={handleFieldClick}
                // style={{ width: "100%", height: "100%" }}
                />
                // )
              ) : (
                <div>
                  <Spin spinning={editorConfigLoading} className={styles.spinWrap}></Spin>
                </div>
              )}
            </div>
          </Content>
        </Layout>

        {/* 域字段信息弹框 */}
        {clickedFieldInfo ? <FieldInfoModal
          visible={domainFieldTypeModalVisible}
          onCancel={() => setStoreState({ domainFieldTypeModalVisible: false })}
          fieldInfo={clickedFieldInfo}
        /> : null}

      </Layout>
    </Drawer>
  );
};

export default Index;
